import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { K } from '../kabinet/atoms';
import { IconClose } from './icons';

/**
 * Окно поверх страницы.
 *
 * ЗАЧЕМ ОКНО, А НЕ ВКЛАДКА ВНИЗУ. Настройка — это отлучка: человек
 * пришёл посмотреть на кампанию, отвлёкся задать лимит и вернулся.
 * Вкладка внизу превращает отлучку в переезд: экран кампании
 * подменяется формой, и, закончив, надо искать, куда вернуться. Окно
 * же само закрывается и оставляет человека там, где он был.
 *
 * РИСУЕТСЯ В КОРНЕ СТРАНИЦЫ. Внутри карточки оно обрезалось бы
 * прокруткой родителя — та же ошибка, что была с увеличением фотографий.
 *
 * ЗАКРЫВАЕТСЯ ПО Esc И ПО ЩЕЛЧКУ МИМО. Окно без выхода — ловушка;
 * а привычка нажимать Esc есть у всех, кто работает с клавиатуры.
 *
 * СТРАНИЦА ПОД ОКНОМ НЕ ПРОКРУЧИВАЕТСЯ. Иначе колесо мыши уводит фон,
 * и, закрыв окно, человек оказывается не там, где был.
 *
 * ═════════════════════════════════════════════════════════════════
 * КРАСНОЙ ШАПКИ ЗДЕСЬ НЕТ, И ЭТО РЕШЕНИЕ ЭТАЛОНА, А НЕ НЕДОДЕЛКА.
 *
 * Требование передачи — «опасные окна отличаются от обычных:
 * красная шапка, знак, обязательный набор слов» — выполнено
 * в админке, во врезках `ОпаснаяВрезка` (см. `ui/danger.tsx`).
 * Здесь, в кабинете, эталон говорит иначе, и спорить с ним
 * мы не стали.
 *
 * В `Bidra-Admin.dc.html` у окна есть поле `danger`, и при
 * `danger:true` меняются рамка, фон шапки и цвет заголовка.
 * В `Bidra-Kabinet.dc.html` такого поля нет вовсе: окно
 * «Удалить правило?» (строка 1988) нарисовано обычным — белая
 * шапка, обычный заголовок, — а опасность вынесена в кнопку:
 * `background:var(--bad-ink);color:#fff`.
 *
 * Разница осмысленная. В админке одно нажатие достаёт до чужих
 * денег и чужого доступа; в кабинете человек распоряжается своим,
 * и красная шапка на каждом «удалить правило» за неделю
 * перестанет что-либо значить.
 *
 * ЕСЛИ ПОНАДОБИТСЯ ОПАСНОЕ ОКНО В КАБИНЕТЕ — брать `ЗнакОпасности`
 * и `.rk-modal-danger` из `ui/danger.tsx` и `styles.css`: правила
 * написаны и сверены сторожем, не хватает только вызова.
 * ═════════════════════════════════════════════════════════════════
 */
/**
 * ═══════════════════════════════════════════════════════════════════════
 * ПОДЛОЖКА БЕЗ РАМКИ — ДЛЯ ОКОН, У КОТОРЫХ РАМКА СВОЯ.
 *
 * `Modal` ниже рисует и подложку, и коробку с шапкой, телом и подвалом.
 * Это верно для наших окон, но не для тех, что вырезаны из эталона
 * целиком: у окна целей своя шапка (название плюс имя кабинета
 * в одной строке), свой подвал с припиской слева и своя ширина 560 —
 * ни одна из трёх наших ступеней ей не равна.
 *
 * Вложить такое окно в `Modal` значило бы получить две шапки и две
 * рамки. Поэтому подложка вынесена отдельно: тот же слой, тот же
 * Escape, тот же запрет прокрутки под окном — и ничего больше.
 * ═══════════════════════════════════════════════════════════════════════
 */
export function Подложка({
  onClose,
  label,
  children,
  имя,
}: {
  onClose: () => void;
  label: string;
  children: React.ReactNode;
  /**
   * Имя узла эталона на самой ПОДЛОЖКЕ, а не на окне.
   *
   * Стенд в этом непоследователен: у «окна целей» `data-d` стоит
   * на окне, а у «окна групп» и «окна колонок» — на затемнении,
   * то есть на `position:fixed; inset:0`. Сверка ходит по именам,
   * и повторять приходится стенд, а не себя: с именем на окне она
   * сравнивала коробку 560 с коробкой во весь экран.
   */
  имя?: string;
}) {
  useEffect(() => {
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', esc);
    const was = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', esc);
      document.body.style.overflow = was;
    };
  }, [onClose]);

  return createPortal(
    /* ═══════════════════════════════════════════════════════════════
       ПОДЛОЖКА — ЧАСТИЦЕЙ ЭТАЛОНА КАБИНЕТА, а не общим `.rk-scrim`.

       Поля у них разные: в кабинете `--sp-8` сверху и `--sp-4`
       по бокам, в админке `--sp-5` кругом. Пока правило было одно
       на двоих, окно целей на 390 выходило 326 пикселей против
       эталонных 358 — то есть на тридцать два уже.

       `.rk-scrim` остаётся первым классом: он держит те же свойства
       для админки и для случая, когда частицы не загрузились.

       ═══════════════════════════════════════════════════════════════
       ОБЛАСТЬ И ЧАСТИЦА — НА РАЗНЫХ УЗЛАХ, И ЭТО ОБЯЗАТЕЛЬНО.

       Правило частицы записано как `[data-kab] .k-подложка-окна` —
       это ПОТОМОК области, а не сама область. Пока `data-kab`
       и класс висели на одном узле, правило не применялось вовсе:
       узел сам себе не потомок. Окно так и оставалось на 32 пикселя
       уже эталонного, а сверка честно это показывала.

       Поэтому область — внешним узлом (пустой блок нулевой высоты
       в потоке `body`), подложка — внутренним. */
    <div data-kab>
    <div data-d={имя} className={`rk-scrim ${K.подложкаОкна}`} onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={label}
        style={{ display: 'contents' }}
        // Щелчок внутри окна не должен его закрывать: иначе набранное
        // в поле теряется от одного промаха мимо кнопки
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
    </div>,
    document.body,
  );
}

export function Modal({
  title,
  onClose,
  children,
  footer,
  width = 1040,
  имя,
  имяОкна,
  классОкна,
  фонТела,
}: {
  title: React.ReactNode;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  width?: number;
  /** Имя узла эталона на ЗАТЕМНЕНИИ — см. довод у `Подложка`. */
  имя?: string;
  /**
   * ═══════════════════════════════════════════════════════════════════
   * ИМЯ И ЧАСТИЦА САМОГО ОКНА — когда у эталона окно РАЗМЕЧЕНО.
   *
   * У большинства окон имени нет: сверяется подложка, а карточка внутри
   * общая. А у окна ABC-теста фото имя есть (`окно-теста-фото`), и своя
   * ширина — 660 против общих ступеней.
   *
   * ЗАЧЕМ ЭТО, А НЕ ВЛОЖИТЬ СВОЮ КАРТОЧКУ ВНУТРЬ. Вложенная даёт
   * КАРТОЧКУ В КАРТОЧКЕ: две рамки, двойные поля и ширина уже эталонной
   * на величину полей `rk-modal-body`. Сверка это и поймала: 316 против
   * 358 на 390.
   * ═══════════════════════════════════════════════════════════════════
   */
  имяОкна?: string;
  классОкна?: string;
  /**
   * ═══════════════════════════════════════════════════════════════════
   * ФОН ТЕЛА ОКНА — КОГДА ВНУТРИ НЕ ФОРМА, А ПАНЕЛИ.
   *
   * Эталон, `featBodyBg`: `featKey === 'clock' ? 'var(--bg-sunken)'
   * : 'transparent'`. Обычное окно — это поля на белом, и белое тело
   * верно. А в окне расписания лежат две карточки («Сетка показов»
   * и «Что было в эти часы») со своим белым фоном и рамкой: на белом
   * теле их рамки пропадают, и две панели читаются как одна лента.
   *
   * ПРОП, А НЕ КЛАСС: значение приходит из эталона переменной, и место
   * ему там же, где ширина, — в вызове, а не в отдельном правиле,
   * которое придётся искать.
   * ═══════════════════════════════════════════════════════════════ */
  фонТела?: string;
}) {
  /**
 * ТРИ ШИРИНЫ, А НЕ ЛЮБАЯ. В образце их ровно три: узкое 520, среднее
 * 760, широкое 1040. Заданное число подтягивается к ближайшей
 * ступени вверх — иначе окна расходятся по ширине на десяток
 * значений, и одинаковые по смыслу выглядят разными.
 */
  const ladder = [520, 760, 1040];
  const step = ladder.find((w) => width <= w) ?? 1040;
  useEffect(() => {
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', esc);
    const was = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', esc);
      document.body.style.overflow = was;
    };
  }, [onClose]);

  return createPortal(
    /* ═══════════════════════════════════════════════════════════════
       ПОДЛОЖКА — ЧАСТИЦЕЙ ЭТАЛОНА КАБИНЕТА, а не общим `.rk-scrim`.

       Поля у них разные: в кабинете `--sp-8` сверху и `--sp-4`
       по бокам, в админке `--sp-5` кругом. Пока правило было одно
       на двоих, окно целей на 390 выходило 326 пикселей против
       эталонных 358 — то есть на тридцать два уже.

       `.rk-scrim` остаётся первым классом: он держит те же свойства
       для админки и для случая, когда частицы не загрузились.

       ═══════════════════════════════════════════════════════════════
       ОБЛАСТЬ И ЧАСТИЦА — НА РАЗНЫХ УЗЛАХ, И ЭТО ОБЯЗАТЕЛЬНО.

       Правило частицы записано как `[data-kab] .k-подложка-окна` —
       это ПОТОМОК области, а не сама область. Пока `data-kab`
       и класс висели на одном узле, правило не применялось вовсе:
       узел сам себе не потомок. Окно так и оставалось на 32 пикселя
       уже эталонного, а сверка честно это показывала.

       Поэтому область — внешним узлом (пустой блок нулевой высоты
       в потоке `body`), подложка — внутренним. */
    <div data-kab>
    <div data-d={имя} className={`rk-scrim ${K.подложкаОкна}`} onClick={onClose}>
      {/* ═══════════════════════════════════════════════════════════
          ЕСТЬ СВОЯ ЧАСТИЦА — ОНА И РЕШАЕТ, а общие ступени отходят.

          У окна ABC-теста фото эталон задаёт и ширину (660 — не из
          лестницы 520 · 760 · 1040), и раскладку (`block`, а не
          колонка). Оставь общее — и вписанный `max-width: 760`
          перебьёт частицу (вписанное всегда сильнее класса), а `flex
          flex-col` перебьёт `display`. Сверка поймала оба: 760 против
          660 и flex против block.
          ═══════════════════════════════════════════════════════════ */}
      <div
        role="dialog"
        aria-modal="true"
        data-d={имяОкна}
        className={классОкна === undefined ? 'rk-modal flex flex-col' : `rk-modal ${классОкна}`}
        style={классОкна === undefined ? { maxWidth: step } : undefined}
        // Щелчок внутри окна не должен его закрывать: иначе набранное
        // в поле теряется от одного промаха мимо кнопки.
        onClick={(e) => e.stopPropagation()}
      >
        <header className="rk-modal-head">
          <span className="flex flex-1 items-center gap-3" style={{ fontSize: 'var(--fs-h2)', lineHeight: 'var(--lh-h2)' }}>
            {title}
          </span>
          <button
            onClick={onClose}
            aria-label="Закрыть"
            title="Закрыть (Esc)"
            className="rk-icon-button"
          >
            <IconClose size={14} />
          </button>
        </header>

        <div
          className="rk-modal-body flex-1 overflow-auto"
          style={фонТела === undefined ? undefined : { background: фонТела }}
        >
          {children}
        </div>

        {footer && (
          <footer className="rk-modal-foot">{footer}</footer>
        )}
      </div>
    </div>
    </div>,
    document.body,
  );
}
